import { VariantAttributesUI } from "./VariantAttributesUI";
import { StandardProps } from "@/components/componentMap";
import {
  ensureIsActive,
  ensureIsActiveVariant,
} from "@/utils/product/ensureIsActive";
import { useVariant } from "@/hooks/useVariant/useVariant";
import { hasAnyStock } from "@/utils/product/hasAnyStock";
import { isPerMetre } from "@/utils/product/isPerMetre";
import { getMinLength } from "@/utils/variant/getMinLength";
import { useEffect, useState } from "react";
import {
  ADD_TO_BASKET_ESI_LENGTH_UPDATED,
  SELECTED_SERVICES_UPDATED,
} from "@/constants/constants";
import { Variant, VariantPrice } from "@/types/types";
import { getColourGroupForVariant } from "@/utils/product/getColourGroupForVariant.ts";

export const VariantAttributes = ({
  product,
  configProps,
}: Pick<StandardProps, "product" | "configProps">) => {
  const isPerMetreProduct = isPerMetre(product);
  const activeProduct = ensureIsActive(product);
  const { variant, isSelected } = useVariant(product);
  const activeVariant = ensureIsActiveVariant(variant);

  const [selectedServicesPrice, setSelectedServicesPrice] = useState();
  const [selectedServicesCount, setSelectedServicesCount] = useState();
  const [lengthSelected, setLengthSelected] = useState(
    isPerMetreProduct ? getMinLength(variant) : undefined,
  );

  useEffect(() => {
    if (isPerMetreProduct) {
      setLengthSelected(getMinLength(variant));
    }
  }, [variant?.id]);

  const hidePriceForSingleSku =
    product.variants.length === 1 &&
    configProps?.hidePriceForSingleSku === true;

  const suppressVariantAttributes =
    configProps?.suppressVariantAttributes === true;

  useEffect(() => {
    const handleSelectedServicesUpdated = (event: CustomEvent) => {
      const { selectedServicesPrice, selectedServicesCount } = event.detail;
      setSelectedServicesPrice(selectedServicesPrice);
      setSelectedServicesCount(selectedServicesCount);
    };

    const handleAddToBasketPerMetreLengthUpdated = (event: CustomEvent) => {
      const { quantity } = event.detail;
      setLengthSelected(quantity);
    };

    window.addEventListener(
      SELECTED_SERVICES_UPDATED,
      handleSelectedServicesUpdated,
    );
    if (isPerMetreProduct) {
      window.addEventListener(
        ADD_TO_BASKET_ESI_LENGTH_UPDATED,
        handleAddToBasketPerMetreLengthUpdated,
      );
    }
    return () => {
      window.removeEventListener(
        SELECTED_SERVICES_UPDATED,
        handleSelectedServicesUpdated,
      );
      if (isPerMetreProduct) {
        window.removeEventListener(
          ADD_TO_BASKET_ESI_LENGTH_UPDATED,
          handleAddToBasketPerMetreLengthUpdated,
        );
      }
    };
  }, []);

  if (suppressVariantAttributes) return null;

  if (!activeProduct || !activeVariant) return null;

  const price = isSelected
    ? activeVariant.price
    : getPriceRange(product, activeVariant);

  const priceFormatted = getFormattedPrice(
    price,
    selectedServicesPrice,
    selectedServicesCount,
    lengthSelected,
  );

  if (!hasAnyStock(product)) {
    return null;
  }

  if (!priceFormatted) return null;

  return (
    <VariantAttributesUI
      price={priceFormatted}
      isPerMetre={isPerMetreProduct}
      hidePriceForSingleSku={hidePriceForSingleSku}
    />
  );
};

const getPriceRange = (product: StandardProps["product"], variant: Variant) => {
  const variantGroup = getColourGroupForVariant(
    product.variantGroups,
    variant.id,
  );

  const display =
    variantGroup?.variantPriceRange?.display ||
    product.variantPriceRange?.display;

  if (display === undefined) return "";

  return display.max === display.min
    ? display.max
    : `${display.min} – ${display.max}`;
};

const getFormattedPrice = (
  price: string | VariantPrice,
  selectedServicesPrice?: string,
  selectedServicesCount?: string,
  lengthSelected?: number,
) => {
  if (!price) return undefined;
  if (typeof price === "string") {
    return price;
  }

  let displayPrice = price?.display;
  if (price?.value && Number(selectedServicesCount) > 0) {
    displayPrice = `£${
      (Number(price.value) + Number(selectedServicesPrice)).toLocaleString(
        "en-GB",
        { minimumFractionDigits: 2, maximumFractionDigits: 2 },
      ) ?? 0
    }${selectedServicesCount ? " (inc. additional services)" : ""}`;
  } else if (price?.value && lengthSelected) {
    displayPrice = `£${(Number(price.value) * lengthSelected).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (for ${lengthSelected}m)`;
  }
  return displayPrice;
};
